import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Plus, Minus, Trash2, Sparkles, ShoppingBag, ArrowRight } from "lucide-react";
import SafeImage from "@/components/SafeImage";

const CATEGORIES = [
  { slug: "cortes", label: "Cortes" },
  { slug: "charcuteria", label: "Charcutería" },
  { slug: "quesos", label: "Quesos" },
  { slug: "complementos", label: "Complementos" },
  { slug: "condimentos", label: "Condimentos" },
  { slug: "extras", label: "Extras" },
];

const BYOP_MIN = 1200;
const BYOP_DISCOUNT = 0.15;

// Key for byop cart deduplication
const byopKey = (p, variantId) => `${p.id}::${variantId || "default"}`;

export default function BuildYourOwnPackagePage() {
  const [activeCat, setActiveCat] = useState("cortes");
  const [products, setProducts] = useState([]);
  const [byopItems, setByopItems] = useState([]); // [{product, variant, quantity}]
  const [loading, setLoading] = useState(true);
  const { addItem, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/products?category=${activeCat}`).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [activeCat]);

  const addToByop = (product, variant) => {
    setByopItems((prev) => {
      const k = byopKey(product, variant?.id);
      const idx = prev.findIndex((x) => byopKey(x.product, x.variant?.id) === k);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, { product, variant, quantity: 1 }];
    });
  };

  const updateQty = (k, delta) => {
    setByopItems((prev) =>
      prev.map((x) => {
        if (byopKey(x.product, x.variant?.id) !== k) return x;
        return { ...x, quantity: Math.max(1, x.quantity + delta) };
      })
    );
  };

  const removeByop = (k) => {
    setByopItems((prev) => prev.filter((x) => byopKey(x.product, x.variant?.id) !== k));
  };

  const subtotal = useMemo(
    () => byopItems.reduce((s, x) => s + (x.variant?.price ?? x.product.price) * x.quantity, 0),
    [byopItems]
  );
  const unlocked = subtotal >= BYOP_MIN;
  const discount = unlocked ? Math.round(subtotal * BYOP_DISCOUNT) : 0;
  const toUnlock = Math.max(0, BYOP_MIN - subtotal);
  const total = subtotal - discount;

  const addAllToCart = () => {
    byopItems.forEach((x) => {
      const v = x.variant;
      const name = v && (x.product.variants?.length || 0) > 1
        ? `${x.product.name} — ${v.label}`
        : x.product.name;
      addItem({
        type: "product",
        id: x.product.id,
        variant_id: v?.id || null,
        name,
        price: v?.price ?? x.product.price,
        image: x.product.image,
        unit: v?.label || x.product.unit,
        quantity: x.quantity,
        _byop: true,  // marker so checkout can pass is_byop=true
      });
    });
    // Mark BYOP mode in sessionStorage so checkout activates 15% discount
    sessionStorage.setItem("arre_byop_active", "1");
    setByopItems([]);
    navigate("/checkout");
  };

  return (
    <div className="bg-arre-black min-h-screen" data-testid="byop-page">
      {/* Hero */}
      <section className="relative py-16 border-b border-arre-border overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&w=1920&q=85')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-arre-black/80 to-arre-black" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-arre-red/60 bg-arre-red/10 text-arre-red text-[10px] tracking-[0.35em] uppercase mb-4">
            <Sparkles className="w-3 h-3" />
            Arma tu combo · 15% off
          </div>
          <h1 className="font-display text-5xl md:text-8xl tracking-wider uppercase text-white leading-[0.85]">
            Arma tu paquete<br />
            <span className="text-arre-red italic font-serif-display normal-case tracking-tight">personalizado</span>
          </h1>
          <p className="font-serif-display italic text-xl md:text-2xl text-white/85 mt-4 max-w-3xl">
            Elige tus cortes, agrega quesos, charcutería y complementos. Al llegar a <span className="text-white font-bold">$1,200 MXN</span> te destrabamos <span className="text-arre-red font-bold">15% de descuento</span> automático.
          </p>
        </div>
      </section>

      {/* Split view */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-0 lg:gap-8 px-6 md:px-12 py-10">
        {/* LEFT — catalog */}
        <div>
          {/* Category tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setActiveCat(c.slug)}
                className={`px-4 py-2 font-display text-sm tracking-widest uppercase border transition ${
                  activeCat === c.slug
                    ? "bg-arre-red border-arre-red text-white"
                    : "border-arre-border text-white/60 hover:text-white hover:border-white/40"
                }`}
                data-testid={`byop-cat-${c.slug}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array(6).fill(0).map((_, i) => (<div key={i} className="aspect-[4/5] bg-arre-surface animate-pulse" />))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {products.map((p) => (
                <ByopProductCard key={p.id} product={p} onAdd={addToByop} />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — sticky cart */}
        <aside className="lg:sticky lg:top-28 self-start">
          <div className="bg-arre-surface border border-arre-border">
            <div className="p-5 border-b border-arre-border">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-arre-red" strokeWidth={1.5} />
                <h2 className="font-display text-2xl tracking-wider uppercase text-white">Tu paquete</h2>
              </div>
              <div className="text-xs text-white/50 mt-1">{byopItems.length} {byopItems.length === 1 ? "item" : "items"}</div>
            </div>

            {/* Progress bar */}
            <div className="p-5 border-b border-arre-border">
              <div className="flex items-center justify-between text-[10px] tracking-widest uppercase mb-2">
                <span className={unlocked ? "text-arre-red font-bold" : "text-white/60"}>
                  {unlocked ? "¡15% OFF DESBLOQUEADO!" : `Faltan ${fmtMXN(toUnlock)} para 15% off`}
                </span>
                <span className="text-white/50">{fmtMXN(subtotal)} / {fmtMXN(BYOP_MIN)}</span>
              </div>
              <div className="h-1.5 bg-arre-black">
                <div
                  className="h-full bg-arre-red transition-all"
                  style={{ width: `${Math.min(100, (subtotal / BYOP_MIN) * 100)}%` }}
                  data-testid="byop-progress"
                />
              </div>
            </div>

            {/* Items */}
            <div className="max-h-80 overflow-y-auto p-5 space-y-3">
              {byopItems.length === 0 && (
                <div className="text-center py-10 text-white/40 text-sm">
                  Empieza agregando cortes desde la izquierda, compare.
                </div>
              )}
              {byopItems.map((x) => {
                const k = byopKey(x.product, x.variant?.id);
                const p = x.variant?.price ?? x.product.price;
                return (
                  <div key={k} className="flex gap-3 items-start" data-testid={`byop-cart-${x.product.slug}`}>
                    <div className="w-12 h-14 bg-arre-black bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url(${x.product.image})` }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm tracking-wider uppercase text-white leading-tight">{x.product.name}</div>
                      <div className="text-[10px] text-white/50">{x.variant?.label || x.product.unit}</div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center border border-arre-border">
                          <button onClick={() => updateQty(k, -1)} className="px-2 py-0.5 text-white hover:bg-arre-black"><Minus className="w-3 h-3" /></button>
                          <span className="px-2 text-white text-sm">{x.quantity}</span>
                          <button onClick={() => updateQty(k, 1)} className="px-2 py-0.5 text-white hover:bg-arre-black"><Plus className="w-3 h-3" /></button>
                        </div>
                        <div className="text-white font-display text-sm">{fmtMXN(p * x.quantity)}</div>
                      </div>
                    </div>
                    <button onClick={() => removeByop(k)} className="text-white/40 hover:text-arre-red"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="p-5 border-t border-arre-border space-y-2 text-sm">
              <div className="flex justify-between text-white/70"><span>Subtotal</span><span>{fmtMXN(subtotal)}</span></div>
              {unlocked && (
                <div className="flex justify-between text-arre-red font-bold" data-testid="byop-discount-row">
                  <span>Combo 15% OFF</span><span>-{fmtMXN(discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-arre-border">
                <span className="font-display text-lg uppercase tracking-wider text-white">Total</span>
                <span className="font-display text-3xl text-white" data-testid="byop-total">{fmtMXN(total)}</span>
              </div>
              <button
                onClick={addAllToCart}
                disabled={byopItems.length === 0}
                className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase py-4 mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="byop-checkout-btn"
              >
                {byopItems.length === 0 ? "Agrega productos" : `Finalizar compra ${unlocked ? `(−15%)` : ""} →`}
              </button>
              {!unlocked && byopItems.length > 0 && (
                <p className="text-[11px] text-white/50 text-center">Puedes seguir armando o comprar sin descuento.</p>
              )}
            </div>
          </div>

          <Link
            to="/paquetes"
            className="block text-center mt-4 text-xs tracking-widest uppercase text-white/50 hover:text-white"
          >
            ¿Mejor ver paquetes ya armados? →
          </Link>
        </aside>
      </div>
    </div>
  );
}

function ByopProductCard({ product, onAdd }) {
  const hasMulti = (product.variants?.length || 0) > 1;
  const [variantId, setVariantId] = useState(product.variants?.[0]?.id || null);
  const selectedVariant = product.variants?.find((v) => v.id === variantId) || product.variants?.[0];
  const price = selectedVariant?.price ?? product.price;

  return (
    <div className="group bg-arre-surface border border-arre-border hover:border-arre-red transition-colors" data-testid={`byop-product-${product.slug}`}>
      <div className="aspect-[4/5] overflow-hidden bg-arre-black">
        <SafeImage src={product.image} alt={product.name} fallbackLabel={product.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" />
      </div>
      <div className="p-4">
        <h4 className="font-display text-lg tracking-wider uppercase text-white leading-none">{product.name}</h4>
        {hasMulti && (
          <div className="flex gap-1 mt-2">
            {product.variants.map((v) => (
              <button
                key={v.id}
                onClick={() => setVariantId(v.id)}
                className={`flex-1 text-[10px] py-1 font-display tracking-widest uppercase border transition ${
                  variantId === v.id ? "border-arre-red bg-arre-red/20 text-arre-red" : "border-arre-border text-white/60"
                }`}
                data-testid={`byop-variant-${product.slug}-${v.id}`}
              >
                {v.id === "350g" ? "350 g" : v.id === "1kg" ? "1 kg" : v.label.split(" ")[0]}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="font-display text-lg text-white">{fmtMXN(price)}</span>
          <button
            onClick={() => onAdd(product, selectedVariant)}
            className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xs tracking-widest uppercase px-3 py-1.5 inline-flex items-center gap-1"
            data-testid={`byop-add-${product.slug}`}
          >
            <Plus className="w-3 h-3" /> Agregar
          </button>
        </div>
      </div>
    </div>
  );
}
